import React, { useCallback, useMemo } from 'react';
import { type UserVideo } from '@/api/videoApi';
import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import { UploadingVideo } from '@/utils/optimistic/optimisticUpload';
import SimpleVideoThumbnail from '@/components/admin/gallery/SimpleVideoThumbnail';
import { VideoRowSkeleton } from '@/components/ui/skeleton/Skeleton';
import { VirtualizedGrid } from '@/components/ui/common';
import { useResponsiveColumns, VIDEO_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';
import UploadingVideoTile from './UploadingVideoTile.tsx';
import QueuedVideoTile from './QueuedVideoTile.tsx';

interface VirtualizedVideosSectionProps {
  /** Filtered videos to display */
  readonly filteredVideos: readonly UserVideo[];
  /** Videos currently in queue */
  readonly queuedVideos?: readonly QueuedVideo[];
  /** Videos currently uploading */
  readonly uploadingVideos?: readonly UploadingVideo[];
  /** Whether initial loading is in progress */
  readonly isLoading?: boolean;
  /** Whether more items are being loaded */
  readonly isLoadingMore?: boolean;
  /** Handler for canceling queued videos */
  readonly cancelQueuedVideo?: (id: string) => void;
  /** Handler for cancel action */
  readonly handleCancel?: () => void;
  /** User email */
  readonly userEmail: string;
  /** Function to get status badge */
  readonly getStatusBadge: (status?: string) => React.ReactElement;
  /** Handler for video click */
  readonly handleVideoClick: (video: UserVideo) => void;
  /** Handler for pin video */
  readonly handlePinVideo: (video: UserVideo, userEmail: string) => void;
  /** Handler for unpin video */
  readonly handleUnpinVideo: (video: UserVideo) => void;
  /** Handler for favourite toggle success */
  readonly onFavouriteSuccess?: (isFavourited: boolean) => void;
  /** Handler for archive toggle success */
  readonly onArchiveSuccess?: (isArchived: boolean) => void;
  /** Whether the current user is an admin (controls archive button visibility) */
  readonly isAdmin?: boolean;
  /** Handler for attaching video last frame */
  readonly onAttachVideoLastFrame?: (video: UserVideo) => Promise<void>;
  /** Custom class name */
  readonly className?: string;
}

/**
 * Virtualized videos section component
 * Handles large video galleries with smooth performance
 * 
 * This component maintains the exact same UI as the original videos section
 * but uses virtualization to only render visible videos
 * 
 * Uses shared components:
 * - VideoRowSkeleton from ui/skeleton for loading states
 * 
 * Features:
 * - Queued and uploading videos at top (non-virtualized, small count)
 * - Regular videos virtualized for 100k+ support
 * - Skeleton loading states
 * - Smooth scrolling
 */
const VirtualizedVideosSection: React.FC<VirtualizedVideosSectionProps> = ({
  filteredVideos,
  queuedVideos = [],
  uploadingVideos = [],
  isLoading = false,
  isLoadingMore = false,
  cancelQueuedVideo,
  handleCancel,
  userEmail,
  getStatusBadge,
  handleVideoClick,
  handlePinVideo,
  handleUnpinVideo,
  onFavouriteSuccess,
  onArchiveSuccess,
  isAdmin = false,
  onAttachVideoLastFrame,
  className = '',
}) => {
  const columns = useResponsiveColumns(VIDEO_GRID_COLUMNS);

  // Combine all items into a unified list for grid layout
  // Order: uploading -> queued -> regular videos
  type GridItem = 
    | { type: 'uploading'; data: UploadingVideo }
    | { type: 'queued'; data: QueuedVideo }
    | { type: 'video'; data: UserVideo };

  const allItems = useMemo<GridItem[]>(() => {
    const items: GridItem[] = [];
    
    // Add uploading videos first
    uploadingVideos.forEach(v => items.push({ type: 'uploading', data: v }));
    
    // Add queued videos
    queuedVideos.forEach(v => items.push({ type: 'queued', data: v }));
    
    // Add regular videos
    filteredVideos.forEach(v => items.push({ type: 'video', data: v }));
    
    return items;
  }, [uploadingVideos, queuedVideos, filteredVideos]);

  const hasAnyContent = allItems.length > 0;

  // Video cards are 16:9 aspect ratio
  // Estimate row height based on typical card dimensions + gap
  const estimatedRowHeight = 260; // ~225px for 16:9 card + 32px gap + padding

  // Memoized video click handler
  const openVideoModal = useCallback((video: UserVideo) => {
    handleVideoClick(video);
  }, [handleVideoClick]);

  // Render function for each grid item
  const renderItem = useCallback((item: GridItem) => {
    if (item.type === 'uploading') {
      return (
        <UploadingVideoTile
          uploadingVideo={item.data}
        />
      );
    }
    if (item.type === 'queued') {
      return (
        <QueuedVideoTile
          queuedVideo={item.data}
          onCancelQueued={cancelQueuedVideo}
          handleCancel={handleCancel}
        />
      );
    }
    // Regular video
    return (
      <SimpleVideoThumbnail
        // isPinnedSection={false}
        video={item.data}
        // onPinVideo={(v) => handlePinVideo(v, userEmail)}
        // onUnpinVideo={handleUnpinVideo}
        onVideoClick={openVideoModal}
        getStatusBadge={getStatusBadge}
        // isAdmin={isAdmin}
        onFavouriteSuccess={onFavouriteSuccess}
        // onArchiveSuccess={onArchiveSuccess}
        onAttachVideoLastFrame={onAttachVideoLastFrame}
      />
    );
  }, [cancelQueuedVideo, handleCancel, handlePinVideo, handleUnpinVideo, openVideoModal, getStatusBadge, userEmail, onFavouriteSuccess, onArchiveSuccess, isAdmin, onAttachVideoLastFrame]);

  // Get unique key for each item
  const getItemKey = useCallback((item: GridItem) => {
    if (item.type === 'uploading') {
      return `uploading-${item.data.id}`;
    }
    if (item.type === 'queued') {
      return `queued-${item.data.id}`;
    }
    return item.data.id;
  }, []);

  // Show skeleton grid during initial loading
  if (isLoading && !hasAnyContent) {
    return (
      <div className={className}>
        <div className="space-y-4">
          {['vid-section-skeleton-0', 'vid-section-skeleton-1', 'vid-section-skeleton-2', 'vid-section-skeleton-3'].map((key, index) => (
            <VideoRowSkeleton key={key} columns={columns} rowIndex={index} />
          ))}
        </div>
      </div>
    );
  }

  // No content to display
  if (!hasAnyContent) {
    return <div className={className} />;
  }

  return (
    <div className={className}>
      {/* Virtualized Grid - All items combined */}
      <VirtualizedGrid
        items={allItems}
        columns={columns}
        estimatedRowHeight={estimatedRowHeight}
        emptyAspectRatio="16/9"
        renderItem={renderItem}
        getItemKey={getItemKey}
      />

      {/* Loading more indicator with skeleton */}
      {isLoadingMore && (
        <div className="mt-4">
          <VideoRowSkeleton columns={columns} />
        </div>
      )}
    </div>
  );
};

export default VirtualizedVideosSection;
